/**
 * The Commission Office's reads: the open market, what the player could sell,
 * and their own listings and sales.
 *
 * Writes go through `useGarage().marketList / marketCancel / marketBuy` — they
 * return the whole garage overview, so the balance and inventory stay right —
 * and this module only has to re-read its lists afterwards (`refreshAll`).
 *
 * Module state, like `state/chat.ts`, so switching tabs does not throw away the
 * filter the player had set.
 */
import { computed, ref, watch } from 'vue';

import {
  ApiError,
  api,
  type MarketBrowse,
  type MarketKind,
  type MarketMine,
  type MarketSellable,
  type MarketSort,
} from '@/net/api';
import { useSession } from '@/state/session';

/** The page's name — one place, matching `MARKET_NAME` on the server. */
export const MARKET_TITLE = 'Commission Office';

/** The About window is open. Shared so the special offer waits behind it. */
export const aboutOpen = ref(false);

const browse = ref<MarketBrowse | null>(null);
const sellable = ref<MarketSellable[] | null>(null);
const mine = ref<MarketMine | null>(null);
const kind = ref<MarketKind | null>(null);
const sort = ref<MarketSort>('newest');
const page = ref(1);
const loading = ref(false);
const error = ref<string | null>(null);

function fail(err: unknown): void {
  error.value = err instanceof ApiError ? err.message : 'The office is not answering.';
}

async function loadBrowse(): Promise<void> {
  loading.value = true;
  try {
    browse.value = await api.marketListings({
      kind: kind.value ?? undefined,
      sort: sort.value,
      page: page.value,
    });
    error.value = null;
  } catch (err) {
    fail(err);
  } finally {
    loading.value = false;
  }
}

async function loadSellable(): Promise<void> {
  try {
    sellable.value = await api.marketSellable();
  } catch (err) {
    fail(err);
  }
}

async function loadMine(): Promise<void> {
  try {
    mine.value = await api.marketMine();
  } catch (err) {
    fail(err);
  }
}

/** After any write: the three lists can all have changed. */
async function refreshAll(): Promise<void> {
  await Promise.all([loadBrowse(), loadSellable(), loadMine()]);
}

function reset(): void {
  browse.value = null;
  sellable.value = null;
  mine.value = null;
  kind.value = null;
  sort.value = 'newest';
  page.value = 1;
  error.value = null;
}

export function useMarket() {
  return {
    browse: computed(() => browse.value),
    sellable: computed(() => sellable.value),
    mine: computed(() => mine.value),
    kind,
    sort,
    page,
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    loadBrowse,
    loadSellable,
    loadMine,
    refreshAll,
    /** Changing a filter goes back to the first page. */
    setKind(next: MarketKind | null): Promise<void> {
      kind.value = next;
      page.value = 1;
      return loadBrowse();
    },
    setSort(next: MarketSort): Promise<void> {
      sort.value = next;
      page.value = 1;
      return loadBrowse();
    },
    setPage(next: number): Promise<void> {
      page.value = Math.max(1, next);
      return loadBrowse();
    },
    dismissError(): void {
      error.value = null;
    },
  };
}

/** Signing out forgets whose market it was. */
const { signedIn } = useSession();
watch(signedIn, (isIn) => {
  if (!isIn) reset();
});
